
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DataTable from "./DataTable";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ContactMessagesAdmin = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentMessage, setCurrentMessage] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("contact_messages")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      console.log("Fetched contact messages:", data);
      setMessages(data || []);
    } catch (error: any) {
      toast({
        title: "Failed to load messages",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (message: any) => {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .update({ read: true })
        .eq("id", message.id);

      if (error) throw error;

      toast({
        title: "Message updated",
        description: "Marked message as read.",
      });

      await fetchMessages();
    } catch (error: any) {
      toast({
        title: "Failed to update message",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (message: any) => {
    if (!confirm("Are you sure you want to delete this message?")) return;

    try {
      const { error } = await supabase
        .from("contact_messages")
        .delete()
        .match({ id: message.id });

      if (error) throw error;

      toast({
        title: "Message deleted",
        description: "The message has been successfully deleted.",
      });

      await fetchMessages();
    } catch (error: any) {
      toast({
        title: "Failed to delete message",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleView = (message: any) => {
    setCurrentMessage(message);
    setOpen(true);
    
    // If message is unread, mark it as read
    if (!message.read) {
      handleMarkAsRead(message);
    }
  };

  const handleReplyViaEmail = () => {
    if (!currentMessage) return;
    
    // Get the email from the current message
    const email = currentMessage.email;
    const subject = "Re: Your message"; // Default subject since we removed the subject field
    
    try {
      // Create the mailto URL with proper encoding
      const mailtoUrl = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}`;
      
      // Open mailto link in the same window instead of a new tab
      window.location.href = mailtoUrl;
      
      console.log("Opening email client with URL:", mailtoUrl);
      
      toast({
        title: "Email client opened",
        description: "Composing reply to " + email,
      });
    } catch (error) {
      console.error("Failed to open email client:", error);
      toast({
        title: "Failed to open email client",
        description: "Please try again or copy the email address manually",
        variant: "destructive",
      });
    }
  };

  const columns = [
    {
      key: "name",
      label: "Name",
    },
    {
      key: "email",
      label: "Email",
    },
    {
      key: "read",
      label: "Status",
      format: (value: boolean) => (
        <Badge variant={value ? "secondary" : "default"}>
          {value ? "Read" : "Unread"}
        </Badge>
      ),
    },
    {
      key: "created_at",
      label: "Received",
      format: (value: string) => format(new Date(value), "MMM d, yyyy"),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Contact Messages</h2>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-pulse">Loading messages...</div>
        </div>
      ) : (
        <DataTable
          data={messages}
          columns={columns}
          onView={handleView}
          onDelete={handleDelete}
        />
      )}
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Message from {currentMessage?.name}</DialogTitle>
            <DialogDescription className="flex justify-between text-foreground/70">
              <span>From: {currentMessage?.name} ({currentMessage?.email})</span>
              <span>{currentMessage?.created_at && format(new Date(currentMessage.created_at), "MMM d, yyyy h:mm a")}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 whitespace-pre-wrap">{currentMessage?.message}</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button 
              variant="default" 
              onClick={handleReplyViaEmail}
            >
              Reply via Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactMessagesAdmin;
